// frontend/src/components/PatternSection.tsx
import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { Problem, apiService, SolutionFeedback, ProgrammingLanguage } from "../services/api";
import Editor from '@monaco-editor/react';
import { useAuth } from "../contexts/FirebaseAuthContext";
import { useUsage } from "../contexts/UsageContext";
import { useAuthModal } from "../hooks/useAuthModal";
import { AuthRequiredModal } from "./AuthRequiredModal";
import { LoginModal } from "./LoginModal";
import { DrawingCanvas } from "./DrawingCanvas";
import { FullscreenCodeModal } from "./FullscreenCodeModal";
import { SimpleUpArrowIcon, SimpleDownArrowIcon, SimpleCheckboxIcon, WorkbenchIcon } from "./CustomIcons";
import { savePendingAction } from "../utils/authRedirectState";
import { logger } from "../services/logger/BrowserLogger";
import { FirstTimeTooltip } from "./FirstTimeTooltip";

// Removed browser Speech Recognition API - using backend transcription only

const getMonacoLanguage = (language: ProgrammingLanguage): string => {
  const mapping = {
    'python': 'python',
    'typescript': 'typescript',
    'java': 'java',
    'golang': 'go'
  };
  return mapping[language] || 'python';
};

const getLanguageDisplayName = (language: ProgrammingLanguage): string => {
  const displayNames = {
    'python': 'Python',
    'typescript': 'TypeScript',
    'java': 'Java',
    'golang': 'Go'
  };
  return displayNames[language] || 'Python';
};

interface VoiceRecording {
  id: string;
  audioBase64: string;
  transcript: string;
  timestamp: number;
}

interface SolutionData {
  text: string;
  currentVoice: VoiceRecording | null;
  voiceHistory: VoiceRecording[];
}

interface DrawingSolutionData {
  objects: any[];
  currentVoice: VoiceRecording | null;
  voiceHistory: VoiceRecording[];
}

interface ProblemSolutionData {
  algorithm: SolutionData;
  code: SolutionData;
  notes: SolutionData;
  drawing: DrawingSolutionData;
}

interface PatternSectionProps {
  pattern: Problem["pattern"];
  problemId: string;
  onSolutionInputToggle?: (isExpanded: boolean) => void;
  // First-time tooltip props
  showWorkbenchTooltip?: boolean;
  workbenchTooltipMessage?: string;
  onWorkbenchTooltipDismiss?: () => void;
}

export const PatternSection: React.FC<PatternSectionProps> = ({
  pattern,
  problemId,
  onSolutionInputToggle,
  showWorkbenchTooltip = false,
  workbenchTooltipMessage = '',
  onWorkbenchTooltipDismiss,
}) => {
  const { userProfile, isAuthenticated, isLoading } = useAuth();
  const { openPaywall, refreshUsage } = useUsage();
  const {
    showAuthRequiredModal,
    showLoginModal,
    requireAuth,
    handleOk,
    handleLoginSuccess: handleAuthLoginSuccess,
    handleClose: handleAuthClose
  } = useAuthModal();
  const [solutionType, setSolutionType] = useState<"code" | "algorithm" | "drawing" | "notes">("algorithm");

  // Initialize problem solution data
  const [problemSolutions, setProblemSolutions] = useState<ProblemSolutionData>({
    algorithm: { text: "", currentVoice: null, voiceHistory: [] },
    code: { text: "", currentVoice: null, voiceHistory: [] },
    notes: { text: "", currentVoice: null, voiceHistory: [] },
    drawing: { objects: [], currentVoice: null, voiceHistory: [] }
  });

  const [isVerifying, setIsVerifying] = useState(false);
  const [feedback, setFeedback] = useState<SolutionFeedback | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [isLoadingSavedSolution, setIsLoadingSavedSolution] = useState(false);
  const [savedSolutionLoaded, setSavedSolutionLoaded] = useState(false);
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [, setAudioBlob] = useState<Blob | null>(null);
  const [transcript, setTranscript] = useState<string>('');
  
  // Drawing states
  const [showDrawing, setShowDrawing] = useState(false);
  
  // Voice playback states
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState<string | null>(null);

  // Local storage cleanup timer
  const [cleanupInterval, setCleanupInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Convenience getter for current solution text
  const solution = solutionType === 'drawing' ? '' : (problemSolutions[solutionType] as SolutionData).text;
  const setSolution = (text: string) => {
    if (solutionType !== 'drawing') {
      setProblemSolutions(prev => ({
        ...prev,
        [solutionType]: { ...prev[solutionType], text }
      }));
    }
  };

  // Drawing modal event handlers
  const handleDrawingBackdropClick = useCallback((e: React.MouseEvent) => {
    // Only close if clicking directly on the backdrop, not its children
    if (e.target === e.currentTarget) {
      setShowDrawing(false);
    }
  }, []);

  const handleDrawingModalClick = useCallback((e: React.MouseEvent) => {
    // Prevent clicks inside the modal from bubbling to the backdrop
    e.stopPropagation();
  }, []);

  // Prevent body scroll and hide floating elements when drawing modal is open
  useEffect(() => {
    if (showDrawing) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('fullscreen-modal-open');
      return () => {
        document.body.style.overflow = 'unset';
        document.body.classList.remove('fullscreen-modal-open');
      };
    }
  }, [showDrawing]);

  // Add global escape key listener for drawing modal
  useEffect(() => {
    if (!showDrawing) return;

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowDrawing(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showDrawing]);

  // Helper function to convert audio blob to base64
  const audioToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:audio/wav;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Local storage functions
  const saveToLocalStorage = (solutionData: ProblemSolutionData) => {
    try {
      const storageKey = `problem-solutions-${problemId}`;
      const dataWithTimestamp = {
        ...solutionData,
        lastUpdated: Date.now()
      };
      localStorage.setItem(storageKey, JSON.stringify(dataWithTimestamp));
    } catch (error) {
      logger.error('Error saving to localStorage', error as Error);
    }
  };

  const loadFromLocalStorage = (): ProblemSolutionData | null => {
    try {
      const storageKey = `problem-solutions-${problemId}`;
      const storedData = localStorage.getItem(storageKey);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        // Remove the timestamp field before returning
        const { lastUpdated, ...solutionData } = parsedData;
        return solutionData;
      }
    } catch (error) {
      logger.error('Error loading from localStorage', error as Error);
    }
    return null;
  };

  const cleanupOldVoiceData = () => {
    try {
      const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000); // 7 days
      const keys = Object.keys(localStorage);
      
      keys.forEach(key => {
        if (key.startsWith('problem-solutions-')) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            if (data.lastUpdated && data.lastUpdated < oneWeekAgo) {
              localStorage.removeItem(key);
            }
          } catch (error) {
            // If data is corrupted, remove it
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      logger.error('Error during voice data cleanup:', error as Error);
    }
  };

  // Helper function to save voice recording to current solution type
  const saveVoiceToSolution = async (audioBlob: Blob, transcript: string) => {
    try {
      const audioBase64 = await audioToBase64(audioBlob);
      const voiceRecording: VoiceRecording = {
        id: `voice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        audioBase64,
        transcript,
        timestamp: Date.now()
      };

      const updatedSolutions = {
        ...problemSolutions,
        [solutionType]: {
          ...problemSolutions[solutionType],
          currentVoice: voiceRecording,
          voiceHistory: problemSolutions[solutionType].currentVoice 
            ? [...problemSolutions[solutionType].voiceHistory, problemSolutions[solutionType].currentVoice]
            : problemSolutions[solutionType].voiceHistory
        }
      };

      setProblemSolutions(updatedSolutions);
      
      // Save to localStorage immediately
      saveToLocalStorage(updatedSolutions);

      // Force a re-render to ensure buttons update immediately
      setTimeout(() => {
        // Force a state update to trigger re-render
        setProblemSolutions(prev => ({ ...prev }));
      }, 50);

      return voiceRecording;
    } catch (error) {
      logger.error('Error converting audio to base64:', error as Error);
      throw error;
    }
  };

  // Add state to track current audio source for stopping
  const [currentAudioSource, setCurrentAudioSource] = useState<AudioBufferSourceNode | null>(null);

  // Helper function to play/stop audio
  const toggleVoicePlayback = (targetSolutionType: "algorithm" | "code" | "drawing") => {
    const currentVoice = problemSolutions[targetSolutionType].currentVoice;
    if (!currentVoice) {
      return;
    }

    // If currently playing this audio, stop it
    if (playingAudio === currentVoice.id && currentAudioSource) {
      currentAudioSource.stop();
      setCurrentAudioSource(null);
      setPlayingAudio(null);
      return;
    }

    // If playing a different audio, stop that first
    if (currentAudioSource) {
      currentAudioSource.stop();
      setCurrentAudioSource(null);
      setPlayingAudio(null);
    }

    // Start new playback
    try {
      const binaryString = atob(currentVoice.audioBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Try using Web Audio API for proper decoding
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContext) {
        const audioContext = new AudioContext();
        
        audioContext.decodeAudioData(bytes.buffer.slice()).then(audioBuffer => {
          const source = audioContext.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(audioContext.destination);
          
          setPlayingAudio(currentVoice.id);
          setCurrentAudioSource(source);
          
          source.onended = () => {
            setPlayingAudio(null);
            setCurrentAudioSource(null);
          };
          
          source.start(0);
          
        }).catch(error => {
          logger.error('Web Audio API decode failed:', error as Error);
          alert('Audio playback not supported - encoding may be incompatible with this browser.');
          setPlayingAudio(null);
          setCurrentAudioSource(null);
        });
      } else {
        alert('Web Audio API not supported in this browser');
      }
      
    } catch (error) {
      logger.error('Error in audio playback:', error as Error);
      alert('Voice playback feature is temporarily disabled while we improve browser compatibility. The audio recording and transcription are working correctly.');
      setPlayingAudio(null);
      setCurrentAudioSource(null);
    }
  };

  // Helper function to show transcript
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _showVoiceTranscript = (targetSolutionType: "algorithm" | "code" | "drawing") => {
    const currentVoice = problemSolutions[targetSolutionType].currentVoice;
    if (!currentVoice) {
      return;
    }

    setShowTranscript(showTranscript === currentVoice.id ? null : currentVoice.id);
  };
  
  // Fullscreen modal state
  const [isFullscreenModalOpen, setIsFullscreenModalOpen] = useState(false);

  // Notify parent when solution input section toggles
  React.useEffect(() => {
    if (onSolutionInputToggle) {
      onSolutionInputToggle(isExpanded);
    }
  }, [isExpanded, onSolutionInputToggle]);

  // Define perform functions with useCallback BEFORE they're used by handlers
  const performVerifySolution = useCallback(async (savedSolution?: string, savedSolutionType?: 'algorithm' | 'code') => {
    setIsExpanded(true); // Expand section immediately so loading indicator is visible
    setIsVerifying(true);
    setFeedback(null);

    try {
      const result = await apiService.verifySolution(problemId, {
        solution: (savedSolution || solution).trim(),
        solutionType: savedSolutionType || solutionType,
      });
      setFeedback(result);
      // Section already expanded above, so user sees loading indicator during verification

      // Refresh usage to update credits display after successful AI feature usage
      refreshUsage().catch(err => {
        logger.warn('Failed to refresh usage after solution verification:', err);
        // Non-critical error - don't show to user
      });
    } catch (error: any) {
      logger.error("Error verifying solution:", error as Error);

      // Check if this is a usage limit error (402 Payment Required)
      const isUsageLimitError = error?.status === 402 ||
                                error?.message?.includes('usage-limit-exceeded') ||
                                error?.originalError?.status === 402;

      if (isUsageLimitError) {
        // Open paywall modal instead of showing generic error
        openPaywall('no-credits', 'solution-verification');
      } else {
        // Show generic error for other errors
        alert("Failed to verify solution. Please try again.");
      }
    } finally {
      setIsVerifying(false);
    }
  }, [problemId, solution, solutionType, refreshUsage, openPaywall]);

  const performSaveAllSolutions = useCallback(async () => {
    setIsSaving(true);

    try {
      const savePromises: Promise<any>[] = [];

      // Save Algorithm if has content
      if (problemSolutions.algorithm.text.trim()) {
        savePromises.push(
          apiService.saveUserSolution(problemId, {
            solutionCode: problemSolutions.algorithm.text.trim(),
            solutionType: 'algorithm',
            notes: ''
          })
        );
      }

      // Save Code if has content
      if (problemSolutions.code.text.trim()) {
        savePromises.push(
          apiService.saveUserSolution(problemId, {
            solutionCode: problemSolutions.code.text.trim(),
            solutionType: 'code',
            notes: ''
          })
        );
      }

      // Save Notes if has content
      if (problemSolutions.notes.text.trim()) {
        savePromises.push(
          apiService.saveUserSolution(problemId, {
            solutionCode: problemSolutions.notes.text.trim(),
            solutionType: 'notes',
            notes: ''
          })
        );
      }

      // Save Drawing if has objects
      if (problemSolutions.drawing.objects.length > 0) {
        savePromises.push(
          apiService.saveUserSolution(problemId, {
            solutionCode: JSON.stringify(problemSolutions.drawing.objects),
            solutionType: 'drawing',
            notes: ''
          })
        );
      }

      // Save all in parallel
      await Promise.all(savePromises);

      setLastSaved(new Date().toLocaleTimeString());

      // Clear the saved indicator after 3 seconds
      setTimeout(() => setLastSaved(null), 3000);
    } catch (error) {
      logger.error("Error saving solutions:", error as Error);
      alert("Failed to save solutions. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [problemId, problemSolutions]);

  // Listen for auth redirect completion to resume pending actions
  React.useEffect(() => {
    const handleAuthRedirect = async (e: Event) => {
      const customEvent = e as CustomEvent;
      const { pendingAction } = customEvent.detail;


      if (pendingAction?.type === 'verify_solution' && pendingAction.data.problemId === problemId) {
        // Resume verify solution with saved data
        await performVerifySolution(pendingAction.data.solution, pendingAction.data.solutionType);
      } else if (pendingAction?.type === 'save_all_solutions' && pendingAction.data.problemId === problemId) {
        // Resume save all solutions
        await performSaveAllSolutions();
      }
    };

    window.addEventListener('auth-redirect-complete', handleAuthRedirect);
    return () => window.removeEventListener('auth-redirect-complete', handleAuthRedirect);
  }, [problemId, performVerifySolution, performSaveAllSolutions]);

  const handleVerifySolution = async () => {
    if (!solution.trim()) {
      alert("Please enter your solution before verifying.");
      return;
    }

    // If not authenticated, save action before redirect
    if (!isAuthenticated) {
      savePendingAction({
        type: 'verify_solution',
        data: {
          problemId,
          solution: solution.trim(),
          solutionType
        }
      });
      // Trigger auth flow (action will resume after redirect)
      requireAuth(() => {});
      return;
    }

    // Already authenticated, proceed immediately
    await performVerifySolution();
  };

  // Save ALL solution types (not just active tab)
  const handleSaveAllSolutions = async () => {
    // If not authenticated, save action before redirect
    if (!isAuthenticated) {
      savePendingAction({
        type: 'save_all_solutions',
        data: { problemId }
      });
      // Trigger auth flow (action will resume after redirect)
      requireAuth(() => {});
      return;
    }

    // Already authenticated, proceed immediately
    await performSaveAllSolutions();
  };

  // Wrap the hook's handleLoginSuccess to trigger save after login
  const handleLoginSuccess = () => {
    handleAuthLoginSuccess();
    // The pending operation in the hook will call performSaveAllSolutions
  };

  // Voice recording functions
  const startVoiceRecording = async () => {
    try {
      // Clear previous transcript
      setTranscript('');
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Initialize MediaRecorder for audio recording with better compatibility
      let recorder: MediaRecorder;
      try {
        // Try to use a more compatible format first
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          recorder = new MediaRecorder(stream, { mimeType: 'audio/mp4' });
        } else {
          // Fallback to default
          recorder = new MediaRecorder(stream);
        }
      } catch (e) {
        logger.warn('Failed to create MediaRecorder with specific format, using default:', e);
        recorder = new MediaRecorder(stream);
      }
      
      const audioChunks: Blob[] = [];
      
      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };
      
      recorder.onstop = async () => {
        const actualMimeType = recorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunks, { type: actualMimeType });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
        
        // Always use backend transcription
        let finalTranscript = 'Voice recording'; // Default fallback
        try {
          // Convert Blob to File for API compatibility
          const fileExtension = actualMimeType.includes('mp4') ? 'mp4' : 'webm';
          const audioFile = new File([audioBlob], `recording.${fileExtension}`, { type: actualMimeType });
          const transcriptionResult = await apiService.transcribeAudio(audioFile);
          
          if (transcriptionResult && transcriptionResult.trim()) {
            finalTranscript = transcriptionResult;
            setTranscript(finalTranscript);
          } else {
            logger.warn('Backend transcription returned empty result');
            setTranscript(finalTranscript);
          }
        } catch (error) {
          logger.error('Backend transcription error:', error as Error);
          setTranscript(finalTranscript);
        }
        
        // Auto-save the recording after transcription is complete
        setTimeout(async () => {
          const currentTranscript = finalTranscript || 'Voice recording';
          if (audioBlob && currentTranscript.trim()) {
            try {
              // Save to current solution type locally
              const voiceRecording = await saveVoiceToSolution(audioBlob, currentTranscript);
              
              // Try to save to backend if authenticated
              if (isAuthenticated) {
                try {
                  const formData = new FormData();
                  const uploadExtension = actualMimeType.includes('mp4') ? 'mp4' : 'webm';
                  formData.append('audio', audioBlob, `voice-solution.${uploadExtension}`);
                  formData.append('transcript', currentTranscript);
                  formData.append('problemId', problemId);
                  formData.append('solutionType', solutionType);
                  formData.append('voiceId', voiceRecording.id);
                  
                  const response = await fetch('/api/solutions/voice', {
                    method: 'POST',
                    body: formData,
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                  });
                  
                  if (response.ok) {
                  } else {
                    logger.warn('Failed to save to backend, but saved locally');
                  }
                } catch (error) {
                  logger.warn('Backend save failed, but saved locally:', error);
                }
              }
              
              // Clear the recording UI
              setTranscript('');
              setAudioBlob(null);
              
            } catch (error) {
              logger.error('Error auto-saving voice recording:', error as Error);
            }
          }
        }, 100); // Small delay to ensure transcript is available
      };
      
      // No browser Speech Recognition - transcription happens after recording via backend
      
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      
    } catch (error) {
      logger.error('Error starting voice recording:', error as Error);
      alert('Unable to access microphone. Please check permissions.');
    }
  };
  
  const stopVoiceRecording = async () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
    }
    setIsRecording(false);
  };
  

  const handleFullscreenToggle = () => {
    setIsFullscreenModalOpen(!isFullscreenModalOpen);
  };

  const loadSavedSolution = React.useCallback(async () => {

    if (!isAuthenticated || savedSolutionLoaded) {
      return;
    }

    setIsLoadingSavedSolution(true);
    try {
      // Load ALL solutions for this problem (algorithm, code, notes, drawing)
      const allSolutions = await apiService.getAllUserSolutionsForProblem(problemId);

      if (allSolutions.length > 0) {
        // Create fresh object to populate each solution type
        setProblemSolutions(prev => {
          const newProblemSolutions = { ...prev };

          allSolutions.forEach(sol => {

            if (sol.solutionType === 'algorithm' || sol.solutionType === 'code' || sol.solutionType === 'notes') {
              newProblemSolutions[sol.solutionType] = {
                text: sol.solutionCode || '',
                currentVoice: null,
                voiceHistory: []
              };
            } else if (sol.solutionType === 'drawing') {
              // Parse drawing objects if stored as JSON string
              try {
                const objects = typeof sol.solutionCode === 'string'
                  ? JSON.parse(sol.solutionCode)
                  : sol.solutionCode;
                newProblemSolutions.drawing = {
                  objects: objects || [],
                  currentVoice: null,
                  voiceHistory: []
                };
              } catch (e) {
                logger.error('Error parsing drawing data:', e);
              }
            }
          });

          return newProblemSolutions;
        });
      } else {
      }
      setSavedSolutionLoaded(true);
    } catch (error) {
      logger.error('Error loading saved solutions:', error as Error);
      setSavedSolutionLoaded(true); // Mark as loaded even if failed to avoid retries
    } finally {
      setIsLoadingSavedSolution(false);
    }
  }, [isAuthenticated, savedSolutionLoaded, problemId]);

  // Load saved solution when expanding and user is authenticated
  React.useEffect(() => {
    if (isExpanded && isAuthenticated && !savedSolutionLoaded) {
      loadSavedSolution();
    }
  }, [isExpanded, isAuthenticated, savedSolutionLoaded, loadSavedSolution]);

  // Load saved solution when component mounts if user is authenticated
  React.useEffect(() => {
    if (!isLoading && isAuthenticated && !savedSolutionLoaded) {
      loadSavedSolution();
    }
  }, [isAuthenticated, savedSolutionLoaded, loadSavedSolution, isLoading]);

  // Initialize local storage data and cleanup on component mount
  React.useEffect(() => {
    // Load existing voice data from localStorage only if we don't have any current voice data
    const storedData = loadFromLocalStorage();
    if (storedData) {
      setProblemSolutions(prev => {
        // Only update if we don't already have voice data
        const hasExistingVoiceData = prev.algorithm.currentVoice || prev.code.currentVoice || prev.drawing.currentVoice;
        if (!hasExistingVoiceData) {
          return storedData;
        }
        return prev;
      });
    }

    // Set up periodic cleanup (run every 24 hours)
    const cleanup = setInterval(cleanupOldVoiceData, 24 * 60 * 60 * 1000);
    setCleanupInterval(cleanup);

    // Run initial cleanup
    cleanupOldVoiceData();

    // Cleanup on unmount
    return () => {
      if (cleanup) {
        clearInterval(cleanup);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemId]);

  // Save to localStorage whenever problemSolutions changes
  React.useEffect(() => {
    saveToLocalStorage(problemSolutions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemSolutions]);

  // Auto-save all solutions to backend (5 seconds after last change)
  React.useEffect(() => {
    if (!isAuthenticated) return;

    const autoSaveTimer = setTimeout(async () => {
      try {
        const savePromises: Promise<any>[] = [];

        // Save Algorithm if has content
        if (problemSolutions.algorithm.text.trim()) {
          savePromises.push(
            apiService.saveUserSolution(problemId, {
              solutionCode: problemSolutions.algorithm.text.trim(),
              solutionType: 'algorithm',
              notes: ''
            })
          );
        }

        // Save Code if has content
        if (problemSolutions.code.text.trim()) {
          savePromises.push(
            apiService.saveUserSolution(problemId, {
              solutionCode: problemSolutions.code.text.trim(),
              solutionType: 'code',
              notes: ''
            })
          );
        }

        // Save Notes if has content
        if (problemSolutions.notes.text.trim()) {
          savePromises.push(
            apiService.saveUserSolution(problemId, {
              solutionCode: problemSolutions.notes.text.trim(),
              solutionType: 'notes',
              notes: ''
            })
          );
        }

        // Save Drawing if has objects
        if (problemSolutions.drawing.objects.length > 0) {
          savePromises.push(
            apiService.saveUserSolution(problemId, {
              solutionCode: JSON.stringify(problemSolutions.drawing.objects),
              solutionType: 'drawing',
              notes: ''
            })
          );
        }

        if (savePromises.length > 0) {
          await Promise.all(savePromises);
        }
      } catch (error) {
        logger.error('❌ Auto-save failed:', error as Error);
        // Fail silently - don't alert user for auto-save failures
      }
    }, 5000); // 5 second debounce

    return () => clearTimeout(autoSaveTimer);
  }, [
    isAuthenticated,
    problemId,
    problemSolutions.algorithm.text,
    problemSolutions.code.text,
    problemSolutions.notes.text,
    problemSolutions.drawing.objects
  ]);

  // Cleanup on component unmount
  React.useEffect(() => {
    return () => {
      if (cleanupInterval) {
        clearInterval(cleanupInterval);
      }
    };
  }, [cleanupInterval]);

  return (
    <div 
      className="pattern-section clickable-section"
      onClick={() => {
        setIsExpanded(!isExpanded);
        onWorkbenchTooltipDismiss?.();
      }}
      title={isExpanded ? "Collapse Solution Section" : "Expand Solution Section"}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        // Only respond to keyboard events if the target is this element or the header
        // Don't respond when focus is on child input elements (textareas, editors)
        const target = e.target as HTMLElement;
        const isChildInput = target.tagName === 'TEXTAREA' ||
                            target.tagName === 'INPUT' ||
                            target.contentEditable === 'true' ||
                            target.closest('.monaco-editor') !== null;

        if (!isChildInput && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          setIsExpanded(!isExpanded);
          onWorkbenchTooltipDismiss?.();
        }
      }}
    >
      <div className="pattern-header">
        <span>
          <WorkbenchIcon size={18} color="#0969da" />
          {" "}Practice Workbench
        </span>
        <div className="workbench-button-wrapper">
          <button
            className="btn-icon"
            onClick={(e) => {
              e.stopPropagation(); // Prevent double-click when clicking the button
              setIsExpanded(!isExpanded);
              onWorkbenchTooltipDismiss?.();
            }}
            type="button"
            title={isExpanded ? "Collapse Solution Section" : "Expand Solution Section"}
          >
            {isExpanded ? (
              <SimpleUpArrowIcon size={16} color="white" />
            ) : (
              <SimpleDownArrowIcon size={16} color="white" />
            )}
          </button>
          <FirstTimeTooltip
            show={showWorkbenchTooltip}
            message={workbenchTooltipMessage}
            position="left"
          />
        </div>
      </div>
      
      {/* Solution Input Section - Collapsible */}
      {isExpanded && (
        <div onClick={(e) => e.stopPropagation()}>
          {/* Loading indicator for saved solutions */}
          {isLoadingSavedSolution && (
            <div className="loading-saved-solution">
              <div className="loading-spinner"></div>
              Loading your saved solution...
            </div>
          )}
          
          <div className="solution-input-section">
            <div className="solution-type-selector">
              <label>
                <input
                  type="radio"
                  value="algorithm"
                  checked={solutionType === "algorithm"}
                  onChange={(e) => setSolutionType(e.target.value as "algorithm" | "code" | "drawing" | "notes")}
                />
                My Algorithm
              </label>
              <label>
                <input
                  type="radio"
                  value="code"
                  checked={solutionType === "code"}
                  onChange={(e) => setSolutionType(e.target.value as "algorithm" | "code" | "drawing" | "notes")}
                />
                My Code
              </label>
              <label>
                <input
                  type="radio"
                  value="notes"
                  checked={solutionType === "notes"}
                  onChange={(e) => setSolutionType(e.target.value as "algorithm" | "code" | "drawing" | "notes")}
                />
                Notes
              </label>
              <label>
                <input
                  type="radio"
                  value="drawing"
                  checked={solutionType === "drawing"}
                  onChange={(e) => setSolutionType(e.target.value as "algorithm" | "code" | "drawing" | "notes")}
                />
                Drawing
              </label>

              {/* Hide voice button for Notes tab (v1) */}
              {solutionType !== 'notes' && (
                <button
                  className={`voice-solution-btn ${isRecording ? 'recording' : ''}`}
                  onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                  type="button"
                  title={isRecording ? "Stop recording" : "Start voice recording"}
                >
                {isRecording ? (
                  <>
                    <span className="recording-icon">⏹️</span>
                    Stop
                  </>
                ) : (
                  <>
                    <span className="microphone-icon">🎤</span>
                    Voice
                  </>
                )}
                </button>
              )}

              {/* Voice Recording Indicator - positioned next to Stop button (hide for Notes) */}
              {solutionType !== 'notes' && isRecording && (
                <div className="voice-recording-indicator">
                  <div className="recording-pulse"></div>
                  <span>Recording...</span>
                </div>
              )}
            </div>
            
            {solutionType === "algorithm" && (
              <div className="algorithm-editor-container">
                <div className="algorithm-editor-header">
                  <span className="algorithm-editor-label">Algorithm Description</span>
                  <div className="header-buttons">
                    <button
                      className="voice-play-btn"
                      onClick={() => toggleVoicePlayback('algorithm')}
                      title={problemSolutions.algorithm.currentVoice && playingAudio === problemSolutions.algorithm.currentVoice.id ? "Stop voice playback" : problemSolutions.algorithm.currentVoice ? "Play voice recording" : "No voice recording"}
                      aria-label={playingAudio === problemSolutions.algorithm.currentVoice?.id ? "Stop algorithm voice playback" : "Play algorithm voice recording"}
                      disabled={!problemSolutions.algorithm.currentVoice}
                    >
                      {problemSolutions.algorithm.currentVoice && playingAudio === problemSolutions.algorithm.currentVoice.id ? '⏹️' : '▶️'}
                    </button>
                    {/* Transcript button removed for v1 */}
                    <button
                      className="save-icon-btn"
                      onClick={handleSaveAllSolutions}
                      disabled={isSaving}
                      title={isSaving ? "Saving..." : "Save all solutions"}
                      aria-label="Save all solutions"
                    >
                      {isSaving ? '⏳' : '💾'}
                    </button>
                    <button
                      className="fullscreen-toggle-btn"
                      onClick={handleFullscreenToggle}
                      title="Open in fullscreen"
                      aria-label="Open algorithm editor in fullscreen"
                    >
                      ⛶
                    </button>
                  </div>
                </div>
                <textarea
                  className="solution-textarea"
                  placeholder="Describe your algorithm approach here..."
                  value={solution}
                  onChange={(e) => setSolution(e.target.value)}
                  rows={12}
                />
              </div>
            )}

            {solutionType === "code" && (
              <div className="code-editor-container">
                <div className="code-editor-header">
                  <span className="code-editor-label">{getLanguageDisplayName(userProfile?.preferredLanguage || 'python')} Code Editor</span>
                  <div className="header-buttons">
                    <button
                      className="voice-play-btn"
                      onClick={() => toggleVoicePlayback('code')}
                      title={problemSolutions.code.currentVoice && playingAudio === problemSolutions.code.currentVoice.id ? "Stop voice playback" : problemSolutions.code.currentVoice ? "Play voice recording" : "No voice recording"}
                      aria-label={playingAudio === problemSolutions.code.currentVoice?.id ? "Stop code voice playback" : "Play code voice recording"}
                      disabled={!problemSolutions.code.currentVoice}
                    >
                      {problemSolutions.code.currentVoice && playingAudio === problemSolutions.code.currentVoice.id ? '⏹️' : '▶️'}
                    </button>
                    {/* Transcript button removed for v1 */}
                    <button
                      className="save-icon-btn"
                      onClick={handleSaveAllSolutions}
                      disabled={isSaving}
                      title={isSaving ? "Saving..." : "Save all solutions"}
                      aria-label="Save all solutions"
                    >
                      {isSaving ? '⏳' : '💾'}
                    </button>
                    <button
                      className="fullscreen-toggle-btn"
                      onClick={handleFullscreenToggle}
                      title="Open in fullscreen"
                      aria-label="Open code editor in fullscreen"
                    >
                      ⛶
                    </button>
                  </div>
                </div>
                <Editor
                  height="500px"
                  defaultLanguage={getMonacoLanguage(userProfile?.preferredLanguage || 'python')}
                  theme="vs-dark"
                  value={solution}
                  onChange={(value) => setSolution(value || "")}
                  options={{
                    minimap: { enabled: false },
                    fontSize: window.innerWidth < 480 ? 12 : 14,
                    lineNumbers: window.innerWidth < 480 ? 'off' : 'on',
                    roundedSelection: false,
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 4,
                    insertSpaces: true,
                    wordWrap: 'on',
                    wrappingIndent: 'indent',
                    suggestOnTriggerCharacters: true,
                    acceptSuggestionOnEnter: 'on',
                    tabCompletion: 'on',
                    folding: false,
                    renderWhitespace: 'none',
                    scrollbar: {
                      vertical: 'auto',
                      horizontal: 'auto',
                      verticalScrollbarSize: window.innerWidth < 480 ? 8 : 14,
                      horizontalScrollbarSize: window.innerWidth < 480 ? 8 : 14,
                    },
                  }}
                  loading={<div className="editor-loading">Loading code editor...</div>}
                />
              </div>
            )}

            {solutionType === "notes" && (
              <div className="notes-editor-container">
                <div className="notes-editor-header">
                  <span className="notes-editor-label">Notes</span>
                  <div className="header-buttons">
                    {/* NO voice/transcript buttons for Notes in v1 */}
                    <button
                      className="save-icon-btn"
                      onClick={handleSaveAllSolutions}
                      disabled={isSaving}
                      title={isSaving ? "Saving..." : "Save all solutions"}
                      aria-label="Save all solutions"
                    >
                      {isSaving ? '⏳' : '💾'}
                    </button>
                    <button
                      className="fullscreen-toggle-btn"
                      onClick={handleFullscreenToggle}
                      title="Open in fullscreen"
                      aria-label="Open notes editor in fullscreen"
                    >
                      ⛶
                    </button>
                  </div>
                </div>
                <textarea
                  className="solution-textarea"
                  placeholder="Write your notes, observations, or key learnings here..."
                  value={solution}
                  onChange={(e) => setSolution(e.target.value)}
                  rows={12}
                />
              </div>
            )}

            {solutionType === "drawing" && (
              <div className="drawing-editor-container">
                <div className="drawing-editor-header">
                  <span className="drawing-editor-label">Drawing Canvas</span>
                  <div className="header-buttons">
                    <button
                      className="voice-play-btn"
                      onClick={() => toggleVoicePlayback('drawing')}
                      title={problemSolutions.drawing.currentVoice && playingAudio === problemSolutions.drawing.currentVoice.id ? "Stop voice playback" : problemSolutions.drawing.currentVoice ? "Play voice recording" : "No voice recording"}
                      aria-label={playingAudio === problemSolutions.drawing.currentVoice?.id ? "Stop drawing voice playback" : "Play drawing voice recording"}
                      disabled={!problemSolutions.drawing.currentVoice}
                    >
                      {problemSolutions.drawing.currentVoice && playingAudio === problemSolutions.drawing.currentVoice.id ? '⏹️' : '▶️'}
                    </button>
                    {/* Transcript button removed for v1 */}
                    <button
                      className="save-icon-btn"
                      onClick={handleSaveAllSolutions}
                      disabled={isSaving}
                      title={isSaving ? "Saving..." : "Save all solutions"}
                      aria-label="Save all solutions"
                    >
                      {isSaving ? '⏳' : '💾'}
                    </button>
                    <button
                      className="fullscreen-toggle-btn"
                      onClick={() => setShowDrawing(true)}
                      title="Open in fullscreen"
                      aria-label="Open drawing canvas in fullscreen"
                    >
                      ⛶
                    </button>
                  </div>
                </div>
                <div className="embedded-drawing-canvas">
                  <DrawingCanvas
                    className="solution-drawing-canvas"
                    initialObjects={problemSolutions.drawing.objects}
                    onObjectsChange={(objects) => {
                      setProblemSolutions(prev => ({
                        ...prev,
                        drawing: {
                          ...prev.drawing,
                          objects
                        }
                      }));
                    }}
                    onSaveDrawing={(imageData) => {
                      // Handle drawing save if needed
                    }}
                  />
                </div>
              </div>
            )}
            
            {/* Only show verify button for code and algorithm tabs */}
            {(solutionType === "code" || solutionType === "algorithm") && (
              <>
                <div className="solution-buttons">
                  <button
                    className="verify-solution-btn"
                    onClick={handleVerifySolution}
                    disabled={isVerifying || !solution.trim()}
                    type="button"
                  >
                    {isVerifying && <span className="inline-spinner"></span>}
                    {isVerifying ? "Verifying..." : `Verify My ${solutionType.charAt(0).toUpperCase() + solutionType.slice(1)}`}
                  </button>

                  {/* Save Solution button removed - replaced by save icon in headers */}
                </div>

                {/* AI Verification Status */}
                {isVerifying && (
                  <div className="verification-status">
                    <span className="status-message">🤖 AI is analyzing your solution...</span>
                    <span className="status-time">Usually takes 10-30 seconds</span>
                  </div>
                )}
              </>
            )}


            {/* Transcript Display */}
            {transcript && (
              <div className="transcript-section">
                <div className="transcript-header">
                  <span>📝 Transcript (Auto-saving...)</span>
                </div>
                <div className="transcript-content">
                  {transcript}
                </div>
              </div>
            )}

            {/* Voice Transcript Modal */}
            {showTranscript && (
              <div className="voice-transcript-modal-backdrop">
                <div className="voice-transcript-modal">
                  <div className="voice-transcript-header">
                    <div className="voice-transcript-title">
                      <span>📄</span>
                      Voice Transcript - {solutionType.charAt(0).toUpperCase() + solutionType.slice(1)} Solution
                    </div>
                    <button
                      className="voice-transcript-close"
                      onClick={() => setShowTranscript(null)}
                      title="Close transcript"
                      aria-label="Close voice transcript"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="voice-transcript-content">
                    {(() => {
                      const currentVoice = problemSolutions[solutionType as keyof typeof problemSolutions].currentVoice;
                      return currentVoice ? currentVoice.transcript : 'No transcript available';
                    })()}
                  </div>
                  <div className="voice-transcript-footer">
                    <div className="voice-transcript-timestamp">
                      {(() => {
                        const currentVoice = problemSolutions[solutionType as keyof typeof problemSolutions].currentVoice;
                        return currentVoice ? `Recorded: ${new Date(currentVoice.timestamp).toLocaleString()}` : '';
                      })()}
                    </div>
                    <button
                      className="voice-transcript-done"
                      onClick={() => setShowTranscript(null)}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Drawing Canvas Section */}
            {showDrawing && (
              <div className="drawing-section">
                <div className="drawing-header">
                  <span>🎨 Visual Solution</span>
                  <button
                    className="close-drawing-btn"
                    onClick={() => setShowDrawing(false)}
                    type="button"
                    title="Close drawing canvas"
                  >
                    ×
                  </button>
                </div>
                <DrawingCanvas
                  onSaveDrawing={(imageData) => {
                    // Handle drawing save if needed
                  }}
                  className="solution-drawing-canvas"
                />
              </div>
            )}

            {/* Save Status */}
            {lastSaved && (
              <div className="save-status">
                <SimpleCheckboxIcon size={16} color="#28a745" /> Solution saved at {lastSaved}
              </div>
            )}
          </div>

          {/* Feedback Section */}
          {feedback && (
            <div className="solution-feedback">
          <h4>📊 Solution Feedback (Score: {feedback.overallScore}/100)</h4>
          
          <div className="feedback-summary">
            <p><strong>Summary:</strong> {feedback.summary}</p>
          </div>

          <div className="feedback-details">
            <div className="feedback-item">
              <h5><SimpleCheckboxIcon size={16} color="#28a745" /> Correctness ({feedback.correctness.score}/100)</h5>
              <p><strong>Is Correct:</strong> {feedback.correctness.isCorrect ? "Yes" : "No"}</p>
              <p>{feedback.correctness.explanation}</p>
            </div>

            {feedback.codeQuality && (
              <div className="feedback-item">
                <h5>🎨 Code Quality ({feedback.codeQuality.score}/100)</h5>
                <p><strong>Readability:</strong> {feedback.codeQuality.readability}</p>
                {feedback.codeQuality.bestPractices.length > 0 && (
                  <div>
                    <strong>Best Practices:</strong>
                    <ul>
                      {feedback.codeQuality.bestPractices.map((practice, idx) => (
                        <li key={idx}>{practice}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {feedback.codeQuality.improvements.length > 0 && (
                  <div>
                    <strong>Improvements:</strong>
                    <ul>
                      {feedback.codeQuality.improvements.map((improvement, idx) => (
                        <li key={idx}>{improvement}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="feedback-item">
              <h5>⚡ Complexity Analysis</h5>
              <p><strong>Time:</strong> {feedback.complexity.timeComplexity}</p>
              <p><strong>Space:</strong> {feedback.complexity.spaceComplexity}</p>
              <p><strong>Is Optimal:</strong> {feedback.complexity.isOptimal ? "Yes" : "No"}</p>
              <p>{feedback.complexity.analysis}</p>
            </div>

            <div className="feedback-item">
              <h5>🧪 Edge Cases</h5>
              <p><strong>Handled Correctly:</strong> {feedback.edgeCases.handledCorrectly ? "Yes" : "No"}</p>
              {feedback.edgeCases.missedCases.length > 0 && (
                <div>
                  <strong>Missed Cases:</strong>
                  <ul>
                    {feedback.edgeCases.missedCases.map((missedCase, idx) => (
                      <li key={idx}>{missedCase}</li>
                    ))}
                  </ul>
                </div>
              )}
              {feedback.edgeCases.suggestions.length > 0 && (
                <div>
                  <strong>Suggestions:</strong>
                  <ul>
                    {feedback.edgeCases.suggestions.map((suggestion, idx) => (
                      <li key={idx}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
      )}

      <AuthRequiredModal
        isOpen={showAuthRequiredModal}
        onClose={handleAuthClose}
        onOk={handleOk}
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={handleAuthClose}
        onSuccess={handleLoginSuccess}
      />

      <FullscreenCodeModal
        isOpen={isFullscreenModalOpen}
        onClose={() => setIsFullscreenModalOpen(false)}
        value={solution}
        onChange={(value) => setSolution(value || "")}
        solutionType={solutionType}
        problemTitle={`Problem ${problemId}`}
      />

      {/* Fullscreen Drawing Modal - Rendered via Portal for true fullscreen */}
      {showDrawing && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-75 z-[9999] fullscreen-drawing-modal-backdrop"
          onClick={handleDrawingBackdropClick}
        >
          <div className="bg-white w-full h-full flex flex-col fullscreen-drawing-modal" onClick={handleDrawingModalClick}>
            <div className="fullscreen-drawing-header p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-xl">🎨</span>
                <span className="font-semibold text-gray-900">
                  Drawing Canvas - Problem {problemId}
                </span>
              </div>
              <button
                className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                onClick={() => setShowDrawing(false)}
                aria-label="Close fullscreen drawing canvas"
                title="Close fullscreen (Esc)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="fullscreen-drawing-content flex-1 min-h-0 overflow-hidden">
              <DrawingCanvas
                className="fullscreen-drawing-canvas"
                initialObjects={problemSolutions.drawing.objects}
                onObjectsChange={(objects) => {
                  setProblemSolutions(prev => ({
                    ...prev,
                    drawing: {
                      ...prev.drawing,
                      objects
                    }
                  }));
                }}
                onSaveDrawing={(imageData) => {
                  // Handle drawing save if needed
                }}
              />
            </div>

            <div className="fullscreen-drawing-footer p-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                <span>💡 Tip: Press <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Esc</kbd> to close</span>
              </div>
              <button
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                onClick={() => setShowDrawing(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
